import { useState, useEffect, useMemo } from "react";
import {
  useCreateVoucher,
  useOneVoucher,
  useUpdateVoucher,
  useNextVoucherNumber,
} from "http-hooks/voucher/useVoucher";
import { useAllBanks } from "http-hooks/bank/useBank";
import { useAllBankPayTps } from "http-hooks/bankpaytp/useBankPayTp";
import { useAllPartySupMasts } from "http-hooks/partysupmast/usepartysupmast";
import Input from "components/ui/Input";
import Select from "components/ui/Select";
import TextArea from "components/ui/TextArea";
import FileUploader from "components/ui/FileUploader";
import useCompanyPreferences from "hooks/useCompanyPreferences";
import Modal from "components/ui/modals";

const toDateInputValue = (date) => {
  if (!date) return "";
  return String(date).split("T")[0];
};

const normalizeVoucherFormData = (voucher = {}) => ({
  docno: voucher.docno || "",
  trdate: toDateInputValue(voucher.trdate),
  trtyp: voucher.trtyp || "MR",
  acdebit: voucher.acdebit || "",
  accredit: voucher.accredit || "",
  tramount: voucher.tramount ?? "",
  naration: voucher.naration || "",
  bankname: voucher.bankname || "",
  docmode: voucher.docmode || "",
  trnid: voucher.trnid || "",
  trndt: toDateInputValue(voucher.trndt),
});

const buildVoucherPayload = (voucherDetails, type) => ({
  docno: voucherDetails.docno,
  trdate: voucherDetails.trdate,
  trtyp: type || voucherDetails.trtyp,
  acdebit: voucherDetails.acdebit,
  accredit: voucherDetails.accredit,
  tramount: Number(voucherDetails.tramount),
  naration: voucherDetails.naration || "",
  bankname: voucherDetails.bankname || "",
  docmode: voucherDetails.docmode || "",
  trnid: voucherDetails.trnid || "",
  trndt: voucherDetails.trndt || "",
});

// JR excludes only CASHAC — BANKAC can be used as the main bank ledger account.
const JR_EXCLUDED_ACCOUNT_IDS = new Set(["CASHAC"]);
const isJournalExcludedAccount = (account) =>
  JR_EXCLUDED_ACCOUNT_IDS.has(account.ps_Id);

// Add this loading skeleton component
const FormSkeleton = () => (
  <div className="animate-pulse">
    <div className="h-7 bg-gray-200 rounded w-1/4 mb-8"></div>
    <div className="space-y-6">
      <div>
        <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
        <div className="h-9 bg-gray-200 rounded w-full"></div>
      </div>
      <div>
        <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
        <div className="h-6 bg-gray-200 rounded w-12"></div>
      </div>
    </div>
  </div>
);

export default function SetVoucher({ id, onClose, type, open }) {
  const [selectedDebitAccount, setSelectedDebitAccount] = useState(null);
  const [selectedCreditAccount, setSelectedCreditAccount] = useState(null);
  const [selectedBank, setSelectedBank] = useState(null);
  const [selectedBankPayTp, setSelectedBankPayTp] = useState(null);
  const [modeOfPayment, setModeOfPayment] = useState("Cash");
  const { isLoading, data } = useOneVoucher(id);
  const { data: voucherNumberData } = useNextVoucherNumber(type || "MR", !id);
  const { isDocUploaderEnabled } = useCompanyPreferences();

  const [voucherDetails, setVoucherDetails] = useState({
    docno: "",
    trdate: "",
    trtyp: type || "MR",
    acdebit: "",
    accredit: "",
    tramount: "",
    naration: "",
    bankname: "",
    docmode: "",
    trnid: "",
    trndt: "",
  });

  const {
    isLoading: isLoadingAllDebitAccount,
    data: { data: allDebitAccountsRaw } = [],
  } = useAllPartySupMasts(
    type !== "JR"
      ? {
          type: "V",
          level: 0,
          grplevel: 0,
        }
      : {
          level: 0,
          grplevel: 0,
          ag_status: 1,
          noPagination: true,
        }
  );
  const {
    isLoading: isLoadingAllCreditAccount,
    data: { data: allCreditAccountsRaw } = [],
  } = useAllPartySupMasts(
    type !== "JR"
      ? {
          type: "C",
          level: 0,
          grplevel: 0,
        }
      : {
          level: 0,
          grplevel: 0,
          ag_status: 1,
          noPagination: true,
        }
  );

  // Exclude only CASHAC for JR (BANKAC is allowed as main bank account).
  const allDebitAccounts = type === "JR"
    ? allDebitAccountsRaw?.filter((account) => !isJournalExcludedAccount(account))
    : allDebitAccountsRaw;

  const allCreditAccounts = type === "JR"
    ? allCreditAccountsRaw?.filter((account) => !isJournalExcludedAccount(account))
    : allCreditAccountsRaw;
  const { isLoading: isLoadingAllBanks, data: { data: allBanks } = [] } =
    useAllBanks();
  const {
    isLoading: isLoadingAllBankPayTps,
    data: { data: allBankPayTps } = [],
  } = useAllBankPayTps();

  const docFilename = useMemo(() => {
    if (!voucherDetails.docno) return "";
    if (type === "JR") return `JRN-${voucherDetails.docno}`;
    return `BD-${voucherDetails.docno}`;
  }, [type, voucherDetails.docno]);

  const { mutateAsync: mutateCreateAsync, isPending: isCreating } = useCreateVoucher();

  const { mutateAsync: mutateUpdateAsync, isPending: isUpdating } = useUpdateVoucher();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setVoucherDetails((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  useEffect(() => {
    setVoucherDetails((prev) => ({
      ...prev,
      acdebit: selectedDebitAccount?.ps_Id,
      accredit: selectedCreditAccount?.ps_Id,
      bankname: selectedBank?.bankname,
      docmode: selectedBankPayTp?.paytype,
    }));
  }, [
    selectedDebitAccount,
    selectedCreditAccount,
    selectedBank,
    selectedBankPayTp,
  ]);

  // Prepare options for Select components
  const modeOfPaymentOptions = [
    { value: "Cash", label: "Cash" },
    { value: "Bank", label: "Bank" }
  ];
  useEffect(() => {
    if (data && id) {
      setVoucherDetails(normalizeVoucherFormData({
        ...data,
        trtyp: data.trtyp || type || "MR",
      }));

      // Set selected accounts for edit mode
      if (data.debitAccount) {
        setSelectedDebitAccount(data.debitAccount);
      }
      if (data.creditAccount) {
        setSelectedCreditAccount(data.creditAccount);
      }
      if (data.bankname && allBanks) {
        const bank = allBanks.find(b => b.bankname === data.bankname);
        if (bank) setSelectedBank(bank);
      }
      if (data.docmode && allBankPayTps) {
        const payType = allBankPayTps.find(p => p.paytype === data.docmode);
        if (payType) setSelectedBankPayTp(payType);
      }
    }
  }, [data, allBanks, allBankPayTps, id, type]);

  useEffect(() => {
    if (!id && voucherNumberData?.nextVoucherNumber) {
      setVoucherDetails((current) => ({
        ...current,
        docno: voucherNumberData.nextVoucherNumber,
      }));
    }
  }, [id, voucherNumberData]);

  const isPending = isCreating || isUpdating;

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = buildVoucherPayload(voucherDetails, type);
      if (id) {
        await mutateUpdateAsync({ id, data: payload });
      } else {
        await mutateCreateAsync(payload);
      }
      onClose();
    } catch (error) {
      console.error("Error submitting voucher:", error);
    }
  };

  return (
    <Modal
      open={open}
      setOpen={(val) => !val && onClose()}
      maxWidth="4xl"
      header={
        <h3 className="text-base font-semibold leading-6 text-gray-900">
          {id ? "Update" : "Add"}{" "}
          {type !== "JR" ? "Money Receipt" : "Journal Voucher"}
        </h3>
      }
      footer={
        <div className="flex justify-end gap-x-3">
          <button
            type="button"
            className="text-sm font-semibold rounded-md px-3 py-2 hover:bg-gray-100 text-gray-900"
            onClick={onClose}
            disabled={isPending}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="set-voucher-form"
            disabled={isPending}
            className="rounded-md bg-gray-700 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isPending ? "Saving..." : "Save"}
          </button>
        </div>
      }
    >
      {id && isLoading ? (
        <FormSkeleton />
      ) : (
      <form id="set-voucher-form" onSubmit={handleFormSubmit}>
      <div className="space-y-8">
        <div className="border-b border-gray-900/10 pb-10">
          <h2 className="text-base font-semibold leading-7 text-gray-900 mt-2 flex items-center justify-center gap-x-6">
          </h2>
          <div className=" mb-2 gap-x-2 gap-y-2 sm:flex sm:items-center sm:justify-between">
            <div>
              <Input
                type="text"
                name="docno"
                id="docno"
                label="Vch No."
                placeholder="12077"
                value={voucherDetails.docno}
                onChange={handleInputChange}
                readOnly={!id}
                isRequired={true}
              />
              {!id && (
                <p className="mt-1 text-xs text-gray-500">
                  {voucherNumberData?.previousVoucherNumber
                    ? `Previous Voucher No.: ${voucherNumberData.previousVoucherNumber}`
                    : "No previous voucher in this financial year."}
                </p>
              )}
            </div>
            <div>
              <Input
                type="date"
                name="trdate"
                id="trdate"
                label="Date"
                value={voucherDetails.trdate}
                onChange={handleInputChange}
                now={!id}
                withinFY={true}
                isRequired={true}
              />
            </div>
          </div>

          <div className="relative">
            <div
              className="absolute inset-0 flex items-center"
              aria-hidden="true"
            >
              <div className="w-full border-t border-gray-300" />
            </div>
          </div>
          {type !== "JR" && (
            <div className="mb-2 flex">
              <div className="w-1/3 flex items-end">
                <label className="block text-sm font-medium leading-6 text-gray-900">
                  Mode of Payment
                </label>
              </div>
              <div className="w-1/2">
                <Select
                  value={modeOfPaymentOptions.find(option => option.value === modeOfPayment)}
                  onChange={(selectedOption) => setModeOfPayment(selectedOption?.value || "Cash")}
                  options={modeOfPaymentOptions}
                  placeholder="Select mode"
                  showLabel={false}
                  isClearable={false}
                />
              </div>
            </div>
          )}
          <div className="relative">
            <div
              className="absolute inset-0 flex items-center"
              aria-hidden="true"
            >
              <div className="w-full border-t border-gray-300" />
            </div>
          </div>
          <div className=" sm:flex">
            <div className="sm:w-2/3 sm:border-r sm:border-gray-300">
              <label
                htmlFor="vouchername"
                className="block text-sm font-medium leading-6 text-gray-900 my-2"
              >
                Particulars
              </label>
              <div className="relative m-2">
                <div
                  className="absolute inset-0 flex items-center w-11/12"
                  aria-hidden="true"
                >
                  <div className="w-full border-t border-gray-300" />
                </div>
              </div>
              <div className="mb-2 mt-4 mr-4">
                <Select
                  value={selectedDebitAccount}
                  onChange={setSelectedDebitAccount}
                  options={allDebitAccounts}
                  getOptionValue={(option) => option?.ps_Id}
                  getOptionLabel={(option) => option?.name}
                  label="Debit A/c"
                  placeholder="Select here"
                  isLoading={isLoadingAllDebitAccount}
                  isClearable={true}
                  isSearchable={true}
                />
              </div>
              <div className="ml-10 mr-4">
                <Select
                  value={selectedCreditAccount}
                  onChange={setSelectedCreditAccount}
                  options={allCreditAccounts}
                  getOptionValue={(option) => option?.ps_Id}
                  getOptionLabel={(option) => option?.name}
                  label="Credit A/c"
                  placeholder="Select here"
                  isLoading={isLoadingAllCreditAccount}
                  isClearable={true}
                  isSearchable={true}
                />
              </div>
              {type !== "JR" && (
                <fieldset className="m-2">
                  <legend className="text-sm font-semibold leading-6 text-gray-900">
                    Select
                  </legend>
                  <div className="grid grid-cols-2">
                    <div className="mt-2 space-y-2 flex items-center space-x-5 sm:space-y-0">
                      <div className="flex items-center">
                        <input
                          id="onAccount"
                          name="onAccount"
                          type="radio"
                          className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"
                        />
                      </div>
                      <label
                        htmlFor="onAccount"
                        className="ml-1 block text-sm font-medium leading-6 text-gray-900"
                      >
                        On Account
                      </label>
                    </div>
                    <div className="mt-2 space-y-2 flex items-center space-x-5 sm:space-y-0">
                      <div className="flex items-center">
                        <input
                          id="againstbill"
                          name="againstbill"
                          type="radio"
                          className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"
                        />
                      </div>
                      <label
                        htmlFor="againstbill"
                        className="ml-1 block text-sm font-medium leading-6 text-gray-900"
                      >
                        Against bill
                      </label>
                    </div>
                  </div>
                </fieldset>
              )}
              {/* <div className="m-2">
                                <label htmlFor="naration" className="block text-sm font-medium leading-6 text-gray-900">
                                    Narration
                                </label>
                                <div className="mt-2">
                                    <input
                                        type="text"
                                        name="naration"
                                        id="naration"
                                        placeholder="Narration"
                                        value={voucherDetails.naration}
                                        onChange={handleInputChange}
                                        className="block w-full rounded-md border-0 p-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                    />
                                </div>
                            </div> */}
              <div className="m-2">
                <TextArea
                  name="naration"
                  label="Narration"
                  value={voucherDetails.naration}
                  onChange={handleInputChange}
                  placeholder="Narration"
                  rows={3}
                />
              </div>
            </div>

            <div className="sm:w-1/3 m-2 ">
              <label
                htmlFor="tramount"
                className="block text-sm font-medium leading-6 text-gray-900"
              >
                Amount
              </label>
              <div className="relative m-2">
                <div
                  className="absolute inset-0 flex items-center"
                  aria-hidden="true"
                >
                  <div className="w-full border-t border-gray-300" />
                </div>
              </div>
              <div>
                <Input
                  type="number"
                  name="tramount"
                  id="tramount"
                  value={voucherDetails.tramount}
                  onChange={handleInputChange}
                  placeholder="0.00"
                  min={0.01}
                  step="0.01"
                  showLabel={false}
                  isRequired={true}
                />
              </div>
            </div>
          </div>
          <div className="relative">
            <div
              className="absolute inset-0 flex items-center"
              aria-hidden="true"
            >
              <div className="w-full border-t border-gray-300" />
            </div>
          </div>

          {(modeOfPayment === "Bank" || type === "JR") && (
            <div className="m-2 gap-2 sm:flex">
              <div>
                <Select
                  value={selectedBank}
                  onChange={setSelectedBank}
                  options={allBanks}
                  getOptionValue={(option) => option?.bnkid}
                  getOptionLabel={(option) => option?.bankname}
                  label="Name of Bank"
                  placeholder="Select here"
                  isLoading={isLoadingAllBanks}
                  isClearable={true}
                  isSearchable={true}
                />
              </div>
              <div>
                <Select
                  value={selectedBankPayTp}
                  onChange={setSelectedBankPayTp}
                  options={allBankPayTps}
                  getOptionValue={(option) => option?.paytyp}
                  getOptionLabel={(option) => option?.paytype}
                  label="Payment Mode"
                  placeholder="Select here"
                  isLoading={isLoadingAllBankPayTps}
                  isClearable={true}
                  isSearchable={true}
                />
              </div>
              <div>
                <div>
                  <Input
                    type="text"
                    name="trnid"
                    id="trnid"
                    label="Chq/DD/TRF No:"
                    value={voucherDetails.trnid}
                    onChange={handleInputChange}
                    placeholder="bankdoc"
                  />
                </div>
              </div>
              <div>
                <div>
                  <Input
                    type="date"
                    name="trndt"
                    id="trndt"
                    label="Chq/DD/TRF Dt:"
                    value={voucherDetails.trndt}
                    onChange={handleInputChange}
                    withinFY={true}
                  />
                </div>
              </div>
            </div>
          )}
          {isDocUploaderEnabled() && docFilename && (
            <div className="sm:col-span-3">
              <FileUploader docFilename={docFilename} />
            </div>
          )}
        </div>
      </div>

      {/* Created/Modified By Information */}
      {id && data && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs text-gray-600">
            {data.usrid && data.datecreate && (
              <div>
                <span className="font-medium">Created By:</span> {data.usrid} on{" "}
                {new Date(data.datecreate).toLocaleString('en-IN', {
                  day: '2-digit', month: '2-digit', year: 'numeric',
                  hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true
                })}
              </div>
            )}
            {data.modusrid && data.moddt && (
              <div>
                <span className="font-medium">Edited By:</span> {data.modusrid} on{" "}
                {new Date(data.moddt).toLocaleString('en-IN', {
                  day: '2-digit', month: '2-digit', year: 'numeric',
                  hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true
                })}
              </div>
            )}
          </div>
        </div>
      )}
      </form>
      )}
    </Modal>
  );
}
